import { ItemsDefinition } from '@perxis-js/perxis-client/dist/items';
import { ReferencesDefinition } from '@perxis-js/perxis-client/dist/references';
import { inject, injectable } from 'inversify';
import {
  Channel,
  Client,
  ClientFactory,
  createChannel,
  createClientFactory,
  DefaultCallOptions,
  Metadata,
  NodeHttpTransport,
  ServiceDefinition,
} from 'nice-grpc-web';

import ConfigService from '../ConfigService';
import NavigationService from '../NavigationService';
import ServiceTypes from '../ServiceTypes';

import { localeMiddleware, loggerMiddleware } from './middlewares';
import type { IGrpcService } from './types';

/**
 * Сервис Grpc
 */
@injectable()
class GrpcService implements IGrpcService {
  _itemsClient?: Client<ItemsDefinition>;

  _referencesClient?: Client<ReferencesDefinition>;

  channel: Channel;

  clientFactory: ClientFactory;

  defaultCallOptions: DefaultCallOptions<ServiceDefinition>;

  constructor(
    @inject(ServiceTypes.Config) private _configService: ConfigService,
    @inject(ServiceTypes.Navigation) private _navigationService: NavigationService,
  ) {
    const { apiRoot, apiKey, locale } = _configService.settings;

    // создаем канал связи
    this.channel = createChannel(
      apiRoot,
      process.env.IS_NODE ? NodeHttpTransport() : undefined,
    );

    // создаем фабрику клиентов
    this.clientFactory = createClientFactory()
      .use(
        localeMiddleware({
          localeId: locale,
        }),
      )
      .use(loggerMiddleware({}));

    // в опциях передаем заголовок авторизации для использования во всех методах
    this.defaultCallOptions = {
      '*': {
        metadata: Metadata({
          authorization: `API-Key ${apiKey}`,
        }),
      },
    };
  }

  /**
   * Возвращает экземпляр клиента для сервиса content.items.Items
   */
  get itemsClient() {
    if (!this._itemsClient) {
      this._itemsClient = this.clientFactory.create(
        ItemsDefinition,
        this.channel,
        this.defaultCallOptions,
      );
    }

    return this._itemsClient;
  }

  /**
   * Возвращает экземпляр клиента для сервиса content.references.ReferencesType
   */
  get referencesClient() {
    if (!this._referencesClient) {
      this._referencesClient = this.clientFactory.create(
        ReferencesDefinition,
        this.channel,
        this.defaultCallOptions,
      );
    }

    return this._referencesClient;
  }
}

export default GrpcService;
